import React from 'react';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { ListDivider } from '../../../../CommonComponents/OPL/List/ListDivider';
import { ModalBody } from '../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Separator } from '../../../../CommonComponents/OPL/Separator/Separator';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { formatDate } from '../../../../CommonUtils/formatDate';
import { formatCurrency } from '../../../../CommonUtils/formatNumber';
import { DepositFrequencyType } from '../../api/useAchFundingOptionsQuery';
import { DepositParams, useDepositMutation } from '../../api/useDepositMutation';
import { DepositMetadata } from './components/DepositForm/useDepositForm';

interface Props {
  onBack(): void;
  onClose(): void;
  onSuccess(): void;
  depositDetails: { params: DepositParams; metadata: DepositMetadata };
}

export const DepositPreview = ({ onClose, onBack, onSuccess, depositDetails }: Props) => {
  const { mutateAsync, isLoading, error } = useDepositMutation();

  const onSubmit = async () => {
    try {
      await mutateAsync(depositDetails.params);

      onSuccess();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <ModalBody>
        {error && <Alert message={error.message} variant="error" mb="16" />}

        <Text color="neutral.500" my="16">
          Please review your transfer details
        </Text>

        <Separator mx={-24} />

        <Box
          as="dl"
          py="16"
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text as="dt" color="neutral.500" fontWeight={500}>
            Transfer from
          </Text>
          <Text as="dd" fontWeight={500}>
            {depositDetails.metadata.sourceAccount}
          </Text>
        </Box>

        <ListDivider mx={-24} />

        {depositDetails.metadata.destinationAccount && (
          <>
            <Box
              as="dl"
              py="16"
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Text as="dt" color="neutral.500" fontWeight={500}>
                Transfer to
              </Text>
              <Text as="dd" fontWeight={500}>
                {depositDetails.metadata.destinationAccount}
              </Text>
            </Box>

            <ListDivider mx={-24} />
          </>
        )}

        <Box
          as="dl"
          py="16"
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text as="dt" color="neutral.500" fontWeight={500}>
            Amount
          </Text>
          <Text as="dd" fontWeight={500}>
            {formatCurrency(parseFloat(depositDetails.params.amount))}
          </Text>
        </Box>

        <ListDivider mx={-24} />

        <Box
          as="dl"
          py="16"
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text as="dt" color="neutral.500" fontWeight={500}>
            Frequency
          </Text>
          <Text as="dd" fontWeight={500}>
            {depositDetails.metadata.scheduledFundingFrequencyLabel}
          </Text>
        </Box>

        <ListDivider mx={-24} />

        {depositDetails.params.scheduledFirstDepositDate && (
          <>
            <Box
              as="dl"
              py="16"
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Text as="dt" color="neutral.500" fontWeight={500}>
                Date of
                {depositDetails.params.scheduledFundingFrequency === DepositFrequencyType.OneTime
                  ? ' '
                  : ' First '}
                Deposit
              </Text>
              <Text as="dd" fontWeight={500}>
                {formatDate(depositDetails.params.scheduledFirstDepositDate)}
              </Text>
            </Box>
            <ListDivider mx={-24} />
          </>
        )}

        {depositDetails.params.scheduledSecondDepositDate && (
          <>
            <Box
              as="dl"
              py="16"
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Text as="dt" color="neutral.500" fontWeight={500}>
                Date of Second Deposit
              </Text>
              <Text as="dd" fontWeight={500}>
                {formatDate(depositDetails.params.scheduledSecondDepositDate)}
              </Text>
            </Box>
            <ListDivider mx={-24} />
          </>
        )}

        <Text fontWeight={600} mt="16">
          Note:
        </Text>
        <Text as="dt" color="neutral.500" fontWeight={500}>
          {depositDetails.metadata.note}
        </Text>
      </ModalBody>

      <ModalFooter>
        <Box flex={1}>
          <Button leftIcon="fas fa-chevron-left" variant="secondary" onClick={onBack}>
            Back
          </Button>
        </Box>

        <Button variant="subtleInactive" onClick={onClose} mr="8">
          Cancel
        </Button>

        <Button onClick={onSubmit} isLoading={isLoading}>
          Transfer
        </Button>
      </ModalFooter>
    </>
  );
};
